import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, ShieldAlert, Eye, EyeOff, CheckCircle2 } from "lucide-react";

export default function AdminForgotPassword() {
  const nav = useNavigate();
  const [f, setF] = useState({ email: "admin@skgrouploans.online", recovery_code: "", new_password: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) return toast.error("Passwords do not match");
    if (f.new_password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", {
        email: f.email, recovery_code: f.recovery_code.trim(), new_password: f.new_password,
      });
      toast.success("Password reset — you can now log in");
      setDone(true);
      setTimeout(() => nav("/admin/login"), 1500);
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-forgot-page" className="min-h-screen bg-[#0B0B0B] flex items-center justify-center px-6 py-16">
      <div className="max-w-md w-full sk-card-dark p-10">
        <div className="flex items-center gap-3 mb-8">
          <img src="/assets/sk-logo.jpeg" alt="SK" className="w-14 h-14 rounded-full ring-1 ring-[#D4AF37]/40"/>
          <div>
            <div className="font-display text-2xl text-white">SK Group</div>
            <div className="text-xs tracking-[0.3em] text-[#D4AF37] uppercase">Password Recovery</div>
          </div>
        </div>

        <div className="inline-flex items-center gap-2 sk-badge sk-badge-gold"><ShieldAlert size={12}/> Recovery mode</div>
        <h1 className="mt-4 font-display text-3xl text-white flex items-center gap-2"><KeyRound size={24} className="text-[#D4AF37]"/> Reset password</h1>
        <p className="text-white/50 text-sm mt-2">Enter your admin email + the server recovery code and choose a new password.</p>

        {done ? (
          <div className="mt-8 p-5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 flex items-center gap-3">
            <CheckCircle2 size={20}/> Reset successful. Redirecting to login...
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5">
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Admin email</div>
              <input data-testid="fp-email" required type="email" className="sk-input" value={f.email} onChange={(e)=>set("email", e.target.value)}/>
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Recovery code</div>
              <input data-testid="fp-code" required className="sk-input font-mono" placeholder="From your server .env (ADMIN_RECOVERY_CODE)" value={f.recovery_code} onChange={(e)=>set("recovery_code", e.target.value)}/>
              <p className="text-[11px] text-white/40 mt-1">Ask your server admin to read the <code>ADMIN_RECOVERY_CODE</code> value from <code>/app/backend/.env</code>.</p>
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-white/50 mb-2">New password (min 8)</div>
              <div className="relative">
                <input data-testid="fp-new" required type={show?"text":"password"} className="sk-input pr-10" value={f.new_password} onChange={(e)=>set("new_password", e.target.value)}/>
                <button type="button" onClick={()=>setShow(!show)} tabIndex={-1} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-white/40 hover:text-white/80">
                  {show ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Confirm new password</div>
              <input data-testid="fp-confirm" required type={show?"text":"password"} className="sk-input" value={f.confirm} onChange={(e)=>set("confirm", e.target.value)}/>
            </label>
            <button data-testid="fp-submit" disabled={busy} className="sk-btn-gold w-full">{busy?"Resetting...":"Reset password"}</button>
          </form>
        )}

        <div className="mt-6 text-center text-sm text-white/60">
          <Link to="/admin/login" className="text-[#D4AF37] hover:underline">← Back to login</Link>
        </div>
      </div>
    </div>
  );
}
